import { useState, useMemo, useEffect } from "react";
import { FieldConfig, formConfig } from "../formConfig";
import { getPersonProfile, photoUrl, PersonProfile } from "../api/people";

interface PersonValue {
  displayName: string;
  jobTitle?: string;
  department?: string;
  id?: string;
  isFreeform?: boolean;
}

function isPerson(v: unknown): v is PersonValue {
  return !!v && typeof v === "object" && "displayName" in (v as object);
}

function Avatar({ id, name, size = 28 }: { id?: string; name: string; size?: number }) {
  const [errored, setErrored] = useState(false);
  const initials = useMemo(() => {
    const parts = name.split(/\s+/).filter(Boolean);
    return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
  }, [name]);
  const style: React.CSSProperties = { width: size, height: size, fontSize: Math.round(size * 0.42) };
  if (id && !errored) {
    return <img className="avatar" style={style} src={photoUrl(id)} alt="" onError={() => setErrored(true)} />;
  }
  return <span className="avatar avatar-initials" style={style}>{initials.toUpperCase() || "?"}</span>;
}

function ProfileCard({ id, fallbackName, onClose }: { id: string; fallbackName: string; onClose: () => void }) {
  const [profile, setProfile] = useState<PersonProfile | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    getPersonProfile(id).then((p) => { setProfile(p); setLoading(false); });
  }, [id]);
  return (
    <div className="profile-overlay" onClick={onClose}>
      <div className="profile-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="profile-close" onClick={onClose} aria-label="Close">×</button>
        <div className="profile-header">
          <Avatar id={id} name={profile?.displayName ?? fallbackName} size={72} />
          <div>
            <div className="profile-name">{profile?.displayName ?? fallbackName}</div>
            {profile?.jobTitle && <div className="profile-title">{profile.jobTitle}</div>}
            {profile?.department && <div className="profile-dept">{profile.department}</div>}
          </div>
        </div>
        {loading && <div className="hint">Loading…</div>}
        {profile && (
          <dl className="profile-details">
            {profile.mail && (<><dt>Email</dt><dd><a href={`mailto:${profile.mail}`}>{profile.mail}</a></dd></>)}
            {profile.mobilePhone && (<><dt>Mobile</dt><dd><a href={`tel:${profile.mobilePhone}`}>{profile.mobilePhone}</a></dd></>)}
            {profile.businessPhones && profile.businessPhones.length > 0 && (<><dt>Office</dt><dd>{profile.businessPhones.join(", ")}</dd></>)}
            {profile.officeLocation && (<><dt>Location</dt><dd>{profile.officeLocation}</dd></>)}
          </dl>
        )}
      </div>
    </div>
  );
}

function PersonDisplay({ value }: { value: unknown }) {
  const [showCard, setShowCard] = useState(false);
  if (!isPerson(value)) {
    if (typeof value === "string" && value) return <span>{value}</span>;
    return <span className="ro-empty">—</span>;
  }
  const { displayName, id, isFreeform } = value;
  if (!id || isFreeform) return <span>{displayName}</span>;
  return (
    <>
      <button type="button" className="ro-person" onClick={() => setShowCard(true)}>
        <Avatar id={id} name={displayName} size={24} />
        <span>{displayName}</span>
      </button>
      {showCard && <ProfileCard id={id} fallbackName={displayName} onClose={() => setShowCard(false)} />}
    </>
  );
}

function formatLikert(v: unknown): { row: string; col: string }[] {
  if (!v || typeof v !== "object") return [];
  const o = v as Record<string, unknown>;
  return Object.entries(o)
    .filter(([, val]) => typeof val === "string" && val !== "")
    .map(([row, col]) => ({ row, col: String(col) }));
}

function formatDate(v: unknown): string {
  if (typeof v !== "string" || !v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString();
}

function FieldValue({ field, value }: { field: FieldConfig; value: unknown }) {
  if (value == null || value === "") return <span className="ro-empty">—</span>;
  switch (field.type) {
    case "textarea":
    case "address":
      return <span className="ro-multi">{String(value)}</span>;
    case "currency": {
      const raw = String(value).replace(/[^\d.]/g, "");
      const num = parseFloat(raw);
      if (isNaN(num)) return <span>{String(value)}</span>;
      return <span>${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
    }
    case "date":
      return <span>{formatDate(value)}</span>;
    case "checkbox":
    case "checkbox_vertical":
      if (Array.isArray(value) && value.length > 0) {
        return (
          <span className="ro-tags">
            {value.map((v) => <span key={String(v)} className="ro-tag">{String(v)}</span>)}
          </span>
        );
      }
      return <span className="ro-empty">—</span>;
    case "likert": {
      const rows = formatLikert(value);
      if (!rows.length) return <span className="ro-empty">—</span>;
      return (
        <ul className="ro-likert">
          {rows.map((r) => (
            <li key={r.row}><span className="ro-likert-row">{r.row}:</span> <strong>{r.col}</strong></li>
          ))}
        </ul>
      );
    }
    case "file": {
      const renderFileLink = (f: { name: string; url: string }) => {
        const downloadUrl = `/api/download?url=${encodeURIComponent(f.url)}`;
        return <a href={downloadUrl} target="_blank" rel="noreferrer">{f.name}</a>;
      };
      if (Array.isArray(value)) {
        const files = value as { name: string; url: string }[];
        if (files.length === 0) return <span className="ro-empty">—</span>;
        return <ul>{files.map((f, i) => <li key={i}>{renderFileLink(f)}</li>)}</ul>;
      }
      if (value && typeof value === "object" && "url" in (value as object)) {
        return renderFileLink(value as { name: string; url: string });
      }
      return <span className="ro-empty">—</span>;
    }
    case "person":
      if (Array.isArray(value)) {
        if (value.length === 0) return <span className="ro-empty">—</span>;
        return (
          <span className="ro-person-list">
            {value.map((v, i) => (
              <PersonDisplay key={i} value={v} />
            ))}
          </span>
        );
      }
      return <PersonDisplay value={value} />;
    default:
      return <span>{String(value)}</span>;
  }
}

function StaticFieldContent({ field }: { field: FieldConfig }) {
  if (field.type === "text_no_input") {
    return <div className="ro-static-text">{field.displayText || <span className="ro-empty">—</span>}</div>;
  }
  if (field.type === "file_no_input") {
    if (!field.attachment) return <span className="ro-empty">—</span>;
    return <a href={`/api/download?url=${encodeURIComponent(field.attachment.url)}`} target="_blank" rel="noreferrer">{field.attachment.name}</a>;
  }
  return null;
}

export default function ReadOnlySubmission({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="ro-submission">
      {formConfig.map((sec) => (
        <section key={sec.title} className="ro-section">
          <h3 className="ro-section-title">{sec.title}</h3>
          <dl className="ro-grid">
            {sec.fields.map((f) => (
              <div key={f.id} className="ro-row">
                <dt>{f.question}</dt>
                <dd>{f.type === "text_no_input" || f.type === "file_no_input" ? <StaticFieldContent field={f} /> : <FieldValue field={f} value={data[f.id]} />}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}
